/**
 * Game Stats Storage Utility
 * All stats are computed from history - history is the source of truth
 */
const ANALYTICS_ENDPOINT = 'https://analytics.totalten.io';

/** Fired after anything is recorded, for views showing a derived total */
export const STATS_EVENT = 'totalten:stats';

const GameStats = {
  HISTORY_KEY: 'gameHistory',
  PLAYER_KEY: 'playerId',

  /**
   * Get all history from storage
   * Structure: { [gameCode]: { attempts: [ts, ...], completes: [ts, ...], clears: [ts, ...] } }
   * @returns {Object}
   */
  getAllHistory() {
    try {
      const stored = localStorage.getItem(this.HISTORY_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error reading history from localStorage:', error);
      return {};
    }
  },

  /**
   * Save history to storage
   * @param {Object} history
   */
  saveHistory(history) {
    try {
      localStorage.setItem(this.HISTORY_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Error writing history to localStorage:', error);
    }
  },

  /**
   * Every stored game, including the tutorial board. Display and stats want
   * realHistory() instead - see there.
   * @returns {Object}
   */
  realHistory() {
    return Object.fromEntries(
      Object.entries(this.getAllHistory()).filter(([, entry]) => !entry.isTutorial)
    );
  },

  /**
   * Record an event for a game
   * @param {Object} game - Game object with encode() method
   * @param {string} eventType - 'attempt', 'complete', or 'cleared'
   * @param {Object} [options]
   * @param {boolean} [options.isTutorial] - the throwaway lesson board, not one
   *   of the player's games. Stored and reported so tutorial completion can be
   *   measured, but filtered out of history and stats.
   * @returns {Object} - { timestamp, gameCode, isFirstTime }
   */
  recordEvent(game, eventType, { isTutorial = false } = {}) {
    const gameCode = game.encode();
    const timestamp = Date.now();
    const historyKey = eventType + 's'; // 'attempts', 'completes', 'clears'

    const history = this.getAllHistory();

    if (!history[gameCode]) {
      history[gameCode] = { attempts: [], completes: [], clears: [] };
    }
    if (isTutorial) {
      history[gameCode].isTutorial = true;
    }

    const isFirstTime = history[gameCode][historyKey].length === 0;
    history[gameCode][historyKey].push(timestamp);
    
    this.saveHistory(history);
    
    console.info(`Recorded ${eventType} for game=${gameCode}, isFirstTime=${isFirstTime}`);
    
    if (localStorage.getItem(this.PLAYER_KEY) == null) {
      const newPlayerId = crypto.randomUUID();
      localStorage.setItem(this.PLAYER_KEY, newPlayerId);
    } 
    fetch(`${ANALYTICS_ENDPOINT}/logGameEvent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        version: 1,
        event: eventType,
        gameCode: gameCode,
        timestamp: timestamp,
        isTutorial: isTutorial,
        playerId: localStorage.getItem(this.PLAYER_KEY) || null
      })
    }).catch(err => {
      console.error('Error logging game event to server:', err);
    });
    // The streak is derived from this history rather than stored, so anything
    // displaying it needs telling that the history just moved
    window.dispatchEvent(new CustomEvent(STATS_EVENT));

    return { timestamp, gameCode, isFirstTime };
  },

  recordAttempt(game, options) {
    return this.recordEvent(game, 'attempt', options);
  },

  recordComplete(game, options) {
    return this.recordEvent(game, 'complete', options);
  },

  recordClear(game, options) {
    return this.recordEvent(game, 'clear', options);
  },

  /**
   * Compute stats from history, optionally filtered by time range
   * The distinct counts bucket each game exactly once - a game that was completed
   * and later cleared counts as cleared only, and distinctAttempt is every game
   * touched in the range, so cleared/complete/unsolved always add back up to it.
   * @param {number} startTime - Optional start timestamp
   * @param {number} endTime - Optional end timestamp
   * @returns {Object} - { attempt, complete, cleared, distinctAttempt, distinctComplete, distinctCleared }
   */
  computeStats(startTime = 0, endTime = Infinity) {
    const history = this.realHistory();
    
    const stats = {
      attempt: 0,
      complete: 0,
      cleared: 0,
      distinctAttempt: 0,
      distinctComplete: 0,
      distinctCleared: 0
    };
    
    Object.values(history).forEach(gameHistory => {
      const attemptsInRange = gameHistory.attempts.filter(t => t >= startTime && t <= endTime);
      const completesInRange = gameHistory.completes.filter(t => t >= startTime && t <= endTime);
      const clearsInRange = gameHistory.clears.filter(t => t >= startTime && t <= endTime);
      
      stats.attempt += attemptsInRange.length;
      stats.complete += completesInRange.length;
      stats.cleared += clearsInRange.length;
      
      const touched = attemptsInRange.length + completesInRange.length + clearsInRange.length > 0;
      if (touched) stats.distinctAttempt++;

      if (clearsInRange.length > 0) {
        stats.distinctCleared++;
      } else if (completesInRange.length > 0) {
        stats.distinctComplete++;
      }
    });
    
    return stats;
  },

  /**
   * Get all-time stats
   * @returns {Object}
   */
  getStats() {
    return this.computeStats();
  },

  /**
   * Get stats for today
   * @returns {Object}
   */
  getTodayStats() {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return this.computeStats(startOfDay);
  },

  /**
   * Get stats for the last N days
   * @param {number} days
   * @returns {Object}
   */
  getStatsLastDays(days) {
    const startTime = Date.now() - (days * 24 * 60 * 60 * 1000);
    return this.computeStats(startTime);
  },

  /**
   * Get history for a specific game code
   * @param {string} gameCode
   * @returns {Object} - { attempts: [...], completes: [...], clears: [...] }
   */
  getGameHistory(gameCode) {
    const history = this.getAllHistory();
    return history[gameCode] || { attempts: [], completes: [], clears: [] };
  },

  /**
   * Search for a game by game object
   * @param {Object} game - Game object with encode() method
   * @returns {{gameCode: string, attempts: number[], completes: number[], clears: number[], firstAttempt: number|null, lastActivity: number|null} | null}
   */
  searchGame(game) {
    const gameCode = game.encode();
    const history = this.getAllHistory();
    const gameHistory = history[gameCode];
    
    if (!gameHistory) {
      return null;
    }
    
    const allTimestamps = [
      ...gameHistory.attempts,
      ...gameHistory.completes,
      ...gameHistory.clears
    ];
    
    return {
      gameCode,
      attempts: gameHistory.attempts,
      completes: gameHistory.completes,
      clears: gameHistory.clears,
      firstAttempt: gameHistory.attempts.length > 0 ? Math.min(...gameHistory.attempts) : null,
      lastActivity: allTimestamps.length > 0 ? Math.max(...allTimestamps) : null
    };
  },

  /**
   * Get every played game, most recently played first
   * @returns {{gameCode: string, attempts: number[], completes: number[], clears: number[], lastActivity: number, status: 'clear'|'complete'|'unsolved'}[]}
   */
  getHistoryEntries() {
    const history = this.realHistory();

    return Object.entries(history)
      .map(([gameCode, gameHistory]) => {
        const { attempts = [], completes = [], clears = [] } = gameHistory;
        const allTimestamps = [...attempts, ...completes, ...clears];

        return {
          gameCode,
          attempts,
          completes,
          clears,
          lastActivity: allTimestamps.length > 0 ? Math.max(...allTimestamps) : 0,
          status: clears.length > 0 ? 'clear' : completes.length > 0 ? 'complete' : 'unsolved'
        };
      })
      .sort((a, b) => b.lastActivity - a.lastActivity);
  },

  /**
   * Get all distinct game codes
   * @param {string} eventType - Optional: filter by 'attempt', 'complete', or 'cleared'
   * @returns {string[]}
   */
  getDistinctGames(eventType = null) {
    const history = this.realHistory();
    
    if (!eventType) {
      return Object.keys(history);
    }
    
    const historyKey = eventType + 's';
    return Object.entries(history)
      .filter(([_, gameHistory]) => gameHistory[historyKey]?.length > 0)
      .map(([gameCode]) => gameCode);
  },

  /**
   * Reset all history
   */
  resetAll() {
    localStorage.removeItem(this.HISTORY_KEY);
  },

  /**
   * Reset history for a specific game
   * @param {string} gameCode
   */
  resetGame(gameCode) {
    const history = this.getAllHistory();
    delete history[gameCode];
    this.saveHistory(history);
  },

  /**
   * Check if localStorage is available
   * @returns {boolean}
   */
  isAvailable() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * Export history for backup
   * @returns {Object}
   */
  exportData() {
    return {
      history: this.getAllHistory(),
      exportedAt: Date.now()
    };
  },

  /**
   * Import history from backup
   * @param {Object} data
   */
  importData(data) {
    if (data.history) {
      this.saveHistory(data.history);
    }
  }
};

export default GameStats;